(() => {
  if (window.cartSubscribeSaveReady) return;
  window.cartSubscribeSaveReady = true;

  const { routes, settings } = window.themeVariables;

  const postCart = async (url, body) => {
    const response = await fetch(`${url}.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    const json = await response.json();
    if (!response.ok || json.status) throw new Error(json.description || json.message || 'cart error');
    return json;
  };

  const lineProperties = (root) => {
    try {
      const parsed = JSON.parse(root.dataset.properties || '{}');
      return Array.isArray(parsed) || !parsed ? {} : parsed;
    } catch {
      return {};
    }
  };

  const CHANGED_KEY = 'subscribe-save:changed';
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const lineSelector = () => (settings.pageType === 'cart' ? 'tr.line-item' : 'cart-drawer .line-item');

  const announce = (message) => {
    let region = document.getElementById('subscribe-save-status');
    if (!region) {
      region = document.createElement('p');
      region.id = 'subscribe-save-status';
      region.className = 'visually-hidden';
      region.setAttribute('role', 'status');
      document.body.append(region);
    }
    region.textContent = '';
    requestAnimationFrame(() => {
      region.textContent = message;
    });
  };

  const highlightLine = (line) => {
    if (!line) return;
    const pill = line.querySelector('[data-subscribe-save-toggle], [data-subscribe-save-select]');
    pill?.focus({ preventScroll: true });
    if (reducedMotion()) return;

    line.querySelectorAll('.price:not(.price--compare)').forEach((price) => {
      price.animate(
        { opacity: [0, 1], transform: ['translateY(8px)', 'translateY(0)'] },
        { duration: 380, delay: 120, easing: 'cubic-bezier(0.25, 1, 0.5, 1)', fill: 'backwards' }
      );
    });
    line.querySelectorAll('.price--compare').forEach((price) => {
      price.animate({ opacity: [0, 1] }, { duration: 300, delay: 320, easing: 'ease-out', fill: 'backwards' });
    });
    line.querySelector('.subscribe-save-cadence')?.animate(
      { opacity: [0, 1], transform: ['translateY(-4px)', 'translateY(0)'] },
      { duration: 300, delay: 120, easing: 'ease-out', fill: 'backwards' }
    );
  };

  const highlightAfterReplace = (oldLine, index) => {
    const startedAt = performance.now();
    const check = () => {
      if (!oldLine.isConnected) {
        highlightLine(document.querySelectorAll(lineSelector())[index]);
        return;
      }
      if (performance.now() - startedAt < 4000) requestAnimationFrame(check);
    };
    requestAnimationFrame(check);
  };

  const setBusy = (root, busy) => {
    root.querySelectorAll('[data-subscribe-save-select], [data-subscribe-save-toggle]').forEach((control) => {
      control.disabled = busy;
    });
    root.setAttribute('aria-busy', String(busy));
    const line = root.closest('line-item, .line-item');
    if (!line) return;
    if (line.tagName === 'LINE-ITEM') {
      if (busy) line.dispatchEvent(new CustomEvent('line-item-quantity:change:start', { detail: { newLineQuantity: Number(root.dataset.quantity) } }));
      else line.querySelector('.line-item__loader')?.setAttribute('hidden', '');
    } else {
      line.style.transition = 'opacity 0.2s ease';
      line.style.opacity = busy ? '0.5' : '';
    }
  };

  const showError = (root, show) => {
    const error = root.querySelector('[data-subscribe-save-error]');
    error.textContent = show ? error.dataset.message : '';
    error.hidden = !show;
  };

  const changePlan = async (root, planId, choice, revert) => {
    const line = root.closest('line-item, .line-item');
    const index = [...document.querySelectorAll(lineSelector())].indexOf(line);
    setBusy(root, true);
    showError(root, false);

    let cart;
    try {
      cart = await postCart(routes.cartChangeUrl, {
        id: root.dataset.lineKey,
        quantity: Number(root.dataset.quantity),
        selling_plan: planId ? Number(planId) : null,
        properties: { ...lineProperties(root), _cart_subscribe_save: planId ? 'true' : null }
      });
    } catch {
      revert();
      setBusy(root, false);
      showError(root, true);
      return;
    }

    const message = root.dataset.updatedMessage.replace('[choice]', choice);

    if (settings.pageType === 'cart') {
      try {
        sessionStorage.setItem(CHANGED_KEY, JSON.stringify({ index, message }));
      } catch {}
      window.location.reload();
      return;
    }

    if (line?.tagName === 'LINE-ITEM') {
      line.dispatchEvent(new CustomEvent('line-item-quantity:change:end', { detail: { cart, newLineQuantity: Number(root.dataset.quantity) } }));
    }
    document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart, replacementDelay: 500 } }));
    if (line && index > -1) highlightAfterReplace(line, index);
    announce(message);
  };

  const highlightAfterReload = () => {
    let changed = null;
    try {
      changed = JSON.parse(sessionStorage.getItem(CHANGED_KEY) || 'null');
      sessionStorage.removeItem(CHANGED_KEY);
    } catch {}
    if (!changed) return;
    highlightLine(document.querySelectorAll(lineSelector())[changed.index]);
    announce(changed.message);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', highlightAfterReload);
  else highlightAfterReload();

  document.addEventListener('change', (event) => {
    const toggle = event.target.closest?.('[data-subscribe-save-toggle]');
    if (toggle) {
      const root = toggle.closest('[data-subscribe-save]');
      const subscribe = toggle.checked;
      changePlan(root, subscribe ? toggle.dataset.plan : null, subscribe ? toggle.dataset.planLabel : root.dataset.oneTimeLabel, () => {
        toggle.checked = !subscribe;
      });
      return;
    }
    const select = event.target.closest?.('[data-subscribe-save-select]');
    if (!select || !select.value) return;
    changePlan(select.closest('[data-subscribe-save]'), select.value === 'one-time' ? null : select.value, select.options[select.selectedIndex].text, () => {
      select.value = select.querySelector('option[selected]')?.value ?? '';
    });
  });
})();
